import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CustomerSearchSelect } from '@/components/ui/customer-search-select';
import { CalendarClock, Send, CheckCircle2, Settings, Play, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABEL = { dh: 'Dentalhygiene', kontrolle: 'Kontrolle' };
const STATUS_BADGE = {
  overdue: { label: 'Überfällig', cls: 'bg-red-100 text-red-700 border-red-200' },
  due_soon: { label: 'Bald fällig', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  ok: { label: 'Geplant', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
};

const DentalRecallPage = () => {
  const [tab, setTab] = useState('overdue');
  const [data, setData] = useState({ items: [], counts: { overdue: 0, due_soon: 0, total: 0 } });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({ dh_interval_months: 6, kontrolle_interval_months: 12, lead_days: 14, second_reminder_days: 14 });
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [newCustomer, setNewCustomer] = useState('');
  const [newType, setNewType] = useState('dh');
  const [newLastVisit, setNewLastVisit] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async (which) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/dental/recall/list?filter=${which}`);
      setData(r.data || { items: [], counts: {} });
    } catch { toast.error('Recall-Liste konnte nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(tab); }, [tab, load]);
  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(Array.isArray(r.data) ? r.data : (r.data?.customers || []))).catch(() => {});
    axios.get(`${API}/dental/recall/settings`).then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const sendInvite = async (cid) => {
    try {
      const r = await axios.post(`${API}/dental/recall/patient/${cid}/send`);
      toast.success(r.data?.second_reminder ? '2. Erinnerung gesendet' : 'Einladung gesendet');
      load(tab);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Versand nicht möglich'); }
  };

  const markComplete = async (cid) => {
    try {
      await axios.post(`${API}/dental/recall/patient/${cid}/complete`);
      toast.success('Besuch erfasst, nächster Recall geplant');
      load(tab);
    } catch { toast.error('Konnte nicht gespeichert werden'); }
  };

  const runAll = async () => {
    if (!window.confirm('Jetzt WhatsApp-Einladungen an alle fälligen Patienten senden?')) return;
    try {
      const r = await axios.post(`${API}/dental/recall/run`);
      toast.success(`Versand: ${r.data?.sent ?? 0} gesendet, ${r.data?.skipped ?? 0} übersprungen`);
      load(tab);
    } catch { toast.error('Lauf fehlgeschlagen'); }
  };

  const saveSettings = async () => {
    try { await axios.put(`${API}/dental/recall/settings`, settings); toast.success('Intervalle gespeichert'); setSettingsOpen(false); }
    catch { toast.error('Speichern fehlgeschlagen'); }
  };

  const createRecall = async () => {
    if (!newCustomer) { toast.error('Bitte Patient wählen'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/dental/recall/patient/${newCustomer}`, {
        recall_type: newType,
        last_visit_date: newLastVisit || null,
        opt_in: true,
      });
      toast.success('Recall eingerichtet');
      setNewCustomer(''); setNewLastVisit('');
      load(tab);
    } catch { toast.error('Konnte nicht gespeichert werden'); }
    finally { setSaving(false); }
  };

  const counts = data.counts || {};

  return (
    <div className="space-y-6" data-testid="dental-recall-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Bell className="text-teal-600" size={24} /> Dental Recall</h1>
          <p className="text-sm text-slate-500">Automatische Erinnerungen an fällige Dentalhygiene- und Kontrolltermine.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runAll} data-testid="recall-run-btn"><Play size={16} className="mr-1" /> Fällige jetzt senden</Button>
          <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="recall-settings-btn"><Settings size={16} className="mr-1" /> Intervalle</Button>
            </DialogTrigger>
            <DialogContent data-testid="recall-settings-dialog">
              <DialogHeader><DialogTitle>Recall-Intervalle</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Dentalhygiene (Monate)</Label><Input type="number" value={settings.dh_interval_months} onChange={(e) => setSettings({ ...settings, dh_interval_months: +e.target.value })} data-testid="recall-set-dh" /></div>
                <div><Label>Kontrolle (Monate)</Label><Input type="number" value={settings.kontrolle_interval_months} onChange={(e) => setSettings({ ...settings, kontrolle_interval_months: +e.target.value })} data-testid="recall-set-kontrolle" /></div>
                <div><Label>Vorlauf für 1. Einladung (Tage)</Label><Input type="number" value={settings.lead_days} onChange={(e) => setSettings({ ...settings, lead_days: +e.target.value })} data-testid="recall-set-lead" /></div>
                <div><Label>2. Erinnerung nach Fälligkeit (Tage)</Label><Input type="number" value={settings.second_reminder_days} onChange={(e) => setSettings({ ...settings, second_reminder_days: +e.target.value })} data-testid="recall-set-second" /></div>
              </div>
              <DialogFooter><Button onClick={saveSettings} data-testid="recall-settings-save">Speichern</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="recall-count-overdue"><CardContent className="p-4"><div className="text-3xl font-bold text-red-600">{counts.overdue ?? 0}</div><div className="text-sm text-slate-500">Überfällig</div></CardContent></Card>
        <Card data-testid="recall-count-duesoon"><CardContent className="p-4"><div className="text-3xl font-bold text-amber-600">{counts.due_soon ?? 0}</div><div className="text-sm text-slate-500">Bald fällig</div></CardContent></Card>
        <Card data-testid="recall-count-total"><CardContent className="p-4"><div className="text-3xl font-bold text-slate-800">{counts.total ?? 0}</div><div className="text-sm text-slate-500">Patienten mit Recall</div></CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Recall für Patient einrichten</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="min-w-[240px]"><Label>Patient</Label><CustomerSearchSelect customers={customers} value={newCustomer} onValueChange={setNewCustomer} placeholder="Patient suchen..." /></div>
          <div><Label>Typ</Label>
            <Select value={newType} onValueChange={setNewType}>
              <SelectTrigger className="w-44" data-testid="recall-new-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="dh">Dentalhygiene</SelectItem><SelectItem value="kontrolle">Kontrolle</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Letzter Besuch</Label><Input type="date" value={newLastVisit} onChange={(e) => setNewLastVisit(e.target.value)} className="w-44" data-testid="recall-new-lastvisit" /></div>
          <Button onClick={createRecall} disabled={saving} data-testid="recall-new-save">Recall anlegen</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList data-testid="recall-tabs">
              <TabsTrigger value="overdue" data-testid="recall-tab-overdue">Überfällig</TabsTrigger>
              <TabsTrigger value="due_soon" data-testid="recall-tab-duesoon">Bald fällig</TabsTrigger>
              <TabsTrigger value="all" data-testid="recall-tab-all">Alle</TabsTrigger>
            </TabsList>
          </Tabs>
        </CardHeader>
        <CardContent>
          {loading ? <div className="py-8 text-center text-slate-400">Lädt…</div> : (
            <Table>
              <TableHeader>
                <TableRow><TableHead>Patient</TableHead><TableHead>Typ</TableHead><TableHead>Letzter Besuch</TableHead><TableHead>Fällig</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aktionen</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {(data.items || []).length === 0 && (<TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-6">Keine Einträge</TableCell></TableRow>)}
                {(data.items || []).map((r) => {
                  const b = STATUS_BADGE[r.status] || STATUS_BADGE.ok;
                  return (
                    <TableRow key={r.customer_id} data-testid={`recall-row-${r.customer_id}`}>
                      <TableCell className="font-medium">{r.patient_name}{!r.phone && <span className="ml-2 text-xs text-red-500">keine Nr.</span>}</TableCell>
                      <TableCell>{TYPE_LABEL[r.recall_type] || r.recall_type}</TableCell>
                      <TableCell>{r.last_visit_date || '—'}</TableCell>
                      <TableCell>{r.next_due_date || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className={b.cls}>{b.label}</Badge></TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button size="sm" variant="outline" disabled={!r.phone} onClick={() => sendInvite(r.customer_id)} data-testid={`recall-send-${r.customer_id}`}><Send size={14} className="mr-1" />Einladen</Button>
                        <Button size="sm" variant="ghost" onClick={() => markComplete(r.customer_id)} data-testid={`recall-complete-${r.customer_id}`}><CheckCircle2 size={14} className="mr-1" />Erledigt</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DentalRecallPage;
